import { useEffect, useState } from "react";
import crudApi from "@serie3/common/api/crudApi";
import MediaplannerGroupEndpoints from "@serie3/mymediaplanner/Modules/Groups/endpoints";

/**
 * The logo of a user-created group, or `fallback` (typically the group's
 * initials) when it has none.
 *
 * Same approach as UserAvatar: the endpoint is authenticated, so the image is
 * fetched as a blob and shown through an object URL, cached per group for the
 * page's lifetime. After an admin changes the logo call invalidateGroupLogo(id).
 */

const cache = new Map(); // groupId -> Promise<string|null>

const fetchLogo = async (groupId) => {
  try {
    const response = await crudApi.getBinary(MediaplannerGroupEndpoints.endpoints.logo + groupId);
    if (response.status !== 200 || !response.data || response.data.size === 0) return null;
    return window.URL.createObjectURL(response.data);
  } catch {
    return null;
  }
};

// Mounted hooks subscribe here so every GroupLogo on the page (sidebar, kanban
// cards, settings) re-fetches after invalidateGroupLogo, without threading a
// version counter through unrelated components.
const listeners = new Set(); // (groupId: string) => void

export const invalidateGroupLogo = (groupId) => {
  const key = String(groupId);
  const pending = cache.get(key);
  cache.delete(key);
  if (pending) pending.then((url) => { if (url) window.URL.revokeObjectURL(url); });
  listeners.forEach((notify) => notify(key));
};

/** url | null (none) | undefined (loading) */
export const useGroupLogo = ({ groupId, version = 0 }) => {
  const key = String(groupId ?? "");
  const [url, setUrl] = useState(undefined);
  const [invalidations, setInvalidations] = useState(0);

  useEffect(() => {
    if (!key) return undefined;
    const notify = (changedKey) => { if (changedKey === key) setInvalidations((n) => n + 1); };
    listeners.add(notify);
    return () => { listeners.delete(notify); };
  }, [key]);

  useEffect(() => {
    let cancelled = false;
    if (!key) {
      setUrl(null);
      return undefined;
    }
    if (!cache.has(key)) cache.set(key, fetchLogo(key));
    setUrl(undefined);
    cache.get(key).then((result) => { if (!cancelled) setUrl(result); });
    return () => { cancelled = true; };
  }, [key, version, invalidations]);

  return url;
};

const GroupLogo = ({ groupId, fallback = null, size = 40, version = 0, style = {}, className = "", title = undefined }) => {
  const url = useGroupLogo({ groupId, version });
  const base = {
    width: size,
    height: size,
    flexShrink: 0,
    overflow: "hidden",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    ...style,
  };

  if (url) {
    return (
      <span className={className} style={base} aria-hidden={title ? undefined : "true"} title={title}>
        <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
      </span>
    );
  }

  return (
    <span className={className} style={base} aria-hidden={title ? undefined : "true"} title={title}>
      {fallback}
    </span>
  );
};

export default GroupLogo;
